/**
 * Privacy Act SOA Service
 * 
 * Service for managing Privacy Act Statement of Applicability (SOA)
 * including fetching controls, tasks, and saving SOA responses.
 * 
 * Created: 2025-11-28
 */

import { db, auth, FirebaseTimestamp } from '@/js/external-js/config/main_config';

// Types
export interface PAControl {
  controlID: string;
  controlName: string;
  controlDescription: string;
  deitID: string;
  implementationGuidance: string;
  paSuggestedAssuranceID: string | string[];
}

export interface PATask {
  assID: string;
  checkDetail: {
    active: string;
    assignedTo: string;
    check: string;
    evidence: string;
    framework: Record<string, { frameName: string }>;
    paDetails?: {
      guidanceNotes: string;
      paCategory: string[];
      paSuggestedEvidence: string;
    };
    procRef?: string[];
  };
  checkFrequency: {
    checkDays: string;
    lastCheck?: Record<string, { checkDate: string; checkStatus: string }>;
    nextCheck: string;
    reminderActive: string;
    reminderDays: string;
    sendReminder: string;
  };
  checkHistory?: Record<string, any>;
  controlID?: string[];
}

export interface SOAAnswer {
  questionId: string;
  controlId: string;
  answer: 'yes' | 'no' | 'unsure';
}

export interface PAControlSOAEntry {
  controlID: string;
  controlName: string;
  controlDesc: string;
  controlApplicable: boolean;
  controlJustification: string;
  implementationStatus: 'Fully implemented' | 'Not implemented' | 'Not applicable';
  framework: string;
  owner: string;
  updatedAt: any;
  updatedBy: string;
}

/**
 * Maps question IDs to control IDs
 */
export const QUESTION_TO_CONTROL_MAP: Record<string, string> = {
  'app-1': 'PA.APP01',
  'app-2': 'PA.APP02',
  'app-3': 'PA.APP03',
  'app-4': 'PA.APP04',
  'app-5': 'PA.APP05',
  'app-6': 'PA.APP06',
  'app-7': 'PA.APP07',
  'app-8': 'PA.APP08',
  'app-9': 'PA.APP09',
  'app-10': 'PA.APP10',
  'app-11': 'PA.APP11',
  'app-12': 'PA.APP12',
  'app-13': 'PA.APP13',
};

/**
 * Fetches all Privacy Act controls from the global compliance collection
 */
export async function fetchPAControls(): Promise<Record<string, PAControl>> {
  try {
    console.log('[PrivacyActSOAService] Fetching PA controls from /compliance/paControls');
    
    const docRef = db.collection('compliance').doc('paControls');
    const docSnap = await docRef.get();
    
    if (!docSnap.exists) {
      console.warn('[PrivacyActSOAService] No PA controls document found');
      return {};
    }
    
    const data = docSnap.data() || {};
    console.log('[PrivacyActSOAService] Fetched PA controls:', Object.keys(data).length);
    
    return data as Record<string, PAControl>;
  } catch (error) {
    console.error('[PrivacyActSOAService] Error fetching PA controls:', error);
    throw error;
  }
}

/**
 * Fetches all Privacy Act assurance tasks from the global tasks collection
 */
export async function fetchPATasks(): Promise<Record<string, PATask>> {
  try {
    console.log('[PrivacyActSOAService] Fetching PA tasks from /tasks/paTasks');
    
    const docRef = db.collection('tasks').doc('paTasks');
    const docSnap = await docRef.get();
    
    if (!docSnap.exists) {
      console.warn('[PrivacyActSOAService] No PA tasks document found');
      return {};
    }
    
    const data = docSnap.data() || {};
    console.log('[PrivacyActSOAService] Fetched PA tasks:', Object.keys(data).length);
    
    return data as Record<string, PATask>;
  } catch (error) {
    console.error('[PrivacyActSOAService] Error fetching PA tasks:', error);
    throw error;
  }
}

/**
 * Gets the suggested assurance task IDs for a control
 */
export function getSuggestedTaskIds(control: PAControl): string[] {
  if (!control.paSuggestedAssuranceID) {
    return [];
  }
  
  if (Array.isArray(control.paSuggestedAssuranceID)) {
    return control.paSuggestedAssuranceID;
  }
  
  // Handle single string value
  return [control.paSuggestedAssuranceID];
}

/**
 * Saves the Privacy Act SOA entries to the customer's SOA document
 * Returns counts of applicable and not applicable controls
 */
export async function savePrivacyActSOA(
  companyId: string,
  answers: SOAAnswer[],
  controls: Record<string, PAControl>
): Promise<{ applicable: number; notApplicable: number }> {
  try {
    console.log('[PrivacyActSOAService] Saving Privacy Act SOA for company:', companyId);
    
    const soaRef = db
      .collection('customers')
      .doc(companyId)
      .collection('product')
      .doc('policyPackages')
      .collection('ISO27001keyDocuments')
      .doc('ismsSoa');
    
    const userId = auth.currentUser?.uid || '';
    const timestamp = FirebaseTimestamp.now();
    
    const soaEntries: Record<string, PAControlSOAEntry> = {};
    
    for (const answer of answers) {
      const controlId = QUESTION_TO_CONTROL_MAP[answer.questionId] || answer.controlId;
      const control = controls[controlId];
      
      if (!control) {
        console.warn(`[PrivacyActSOAService] Control not found for ID: ${controlId}`);
        continue;
      }
      
      // Determine applicability based on answer
      // Yes = applicable, No/Unsure = not applicable
      const isApplicable = answer.answer === 'yes';
      
      const entry: PAControlSOAEntry = {
        controlID: controlId,
        controlName: control.controlName,
        controlDesc: control.controlDescription,
        controlApplicable: isApplicable,
        controlJustification: isApplicable 
          ? control.implementationGuidance 
          : 'Control marked as not applicable based on SOA questionnaire response.',
        implementationStatus: isApplicable ? 'Fully implemented' : 'Not applicable',
        framework: 'privacyAct',
        owner: userId,
        updatedAt: timestamp,
        updatedBy: userId,
      };
      
      soaEntries[controlId] = entry;
    }
    
    // Merge with existing SOA document
    await soaRef.set(soaEntries, { merge: true });
    
    // Count applicable vs not applicable
    const applicable = Object.values(soaEntries).filter(e => e.controlApplicable).length;
    const notApplicable = Object.values(soaEntries).filter(e => !e.controlApplicable).length;
    
    console.log('[PrivacyActSOAService] Saved SOA entries - applicable:', applicable, 'not applicable:', notApplicable);
    return { applicable, notApplicable };
  } catch (error) {
    console.error('[PrivacyActSOAService] Error saving Privacy Act SOA:', error);
    throw error;
  }
}

/**
 * Creates assurance tasks for applicable controls
 */
export async function createPrivacyActAssuranceTasks(
  companyId: string,
  answers: SOAAnswer[],
  controls: Record<string, PAControl>,
  globalTasks: Record<string, PATask>
): Promise<{ created: number; skipped: number }> {
  try {
    console.log('[PrivacyActSOAService] Creating assurance tasks for company:', companyId);
    
    const assuranceRef = db
      .collection('customers')
      .doc(companyId)
      .collection('compliance')
      .doc('assurance');
    
    const userId = auth.currentUser?.uid || '';
    const timestamp = FirebaseTimestamp.now();
    
    // Get existing tasks to avoid duplicates
    const existingDoc = await assuranceRef.get();
    const existingTasks = existingDoc.exists ? existingDoc.data() || {} : {};
    const existingTaskIds = new Set(Object.keys(existingTasks));
    
    const tasksToCreate: Record<string, any> = {};
    let created = 0;
    let skipped = 0;
    
    for (const answer of answers) {
      // Only create tasks for Yes answers (No and Unsure = not applicable)
      if (answer.answer !== 'yes') {
        continue;
      }
      
      const controlId = QUESTION_TO_CONTROL_MAP[answer.questionId] || answer.controlId;
      const control = controls[controlId];
      
      if (!control) {
        console.warn(`[PrivacyActSOAService] Control not found for ID: ${controlId}`);
        continue;
      }
      
      // Get suggested task IDs for this control
      const suggestedTaskIds = getSuggestedTaskIds(control);
      
      for (const taskId of suggestedTaskIds) {
        // Skip if task already exists
        if (existingTaskIds.has(taskId)) {
          console.log(`[PrivacyActSOAService] Task ${taskId} already exists, skipping`);
          skipped++;
          continue;
        }
        
        // Get the global task template
        const globalTask = globalTasks[taskId];
        if (!globalTask) {
          console.warn(`[PrivacyActSOAService] Global task not found: ${taskId}`);
          continue;
        }
        
        // Calculate next check date (365 days from now by default)
        const checkDays = parseInt(globalTask.checkFrequency?.checkDays || '365', 10);
        const nextCheckDate = new Date();
        nextCheckDate.setDate(nextCheckDate.getDate() + checkDays);
        
        // Create the task entry
        tasksToCreate[taskId] = {
          assID: taskId,
          checkDetail: {
            active: 'true',
            assignedTo: userId,
            check: globalTask.checkDetail?.check || '',
            evidence: globalTask.checkDetail?.evidence || 'Upload Evidence',
            framework: {
              '0': {
                frameName: 'Privacy Act',
                controls: [controlId]
              }
            },
            paDetails: globalTask.checkDetail?.paDetails || {},
            procRef: globalTask.checkDetail?.procRef || [controlId],
          },
          checkFrequency: {
            checkDays: globalTask.checkFrequency?.checkDays || '365',
            reminderDays: globalTask.checkFrequency?.reminderDays || '7',
            sendReminder: 'false',
            nextCheck: FirebaseTimestamp.fromDate(nextCheckDate),
          },
          checkHistory: {
            numberPreviousChecks: '0'
          },
          taskCreationMetadata: {
            createdBy: userId,
            createdAt: timestamp,
            updatedAt: timestamp,
            source: 'PrivacyActSOABuilder'
          },
          controlID: [controlId]
        };
        
        existingTaskIds.add(taskId); // Prevent duplicates within this batch
        created++;
      }
    }
    
    // Save all tasks in one batch
    if (Object.keys(tasksToCreate).length > 0) {
      await assuranceRef.set(tasksToCreate, { merge: true });
      console.log('[PrivacyActSOAService] Created', created, 'assurance tasks');
    }
    
    return { created, skipped };
  } catch (error) {
    console.error('[PrivacyActSOAService] Error creating assurance tasks:', error);
    throw error;
  }
}

/**
 * Complete Privacy Act SOA builder flow
 * Saves SOA entries and creates assurance tasks
 */
export async function completePrivacyActSOA(
  companyId: string,
  answers: SOAAnswer[]
): Promise<{ soaSaved: boolean; controlsApplicable: number; controlsNotApplicable: number; tasksCreated: number; tasksSkipped: number }> {
  try {
    console.log('[PrivacyActSOAService] Starting complete Privacy Act SOA flow');
    
    // Fetch controls and tasks in parallel
    const [controls, tasks] = await Promise.all([
      fetchPAControls(),
      fetchPATasks()
    ]);
    
    // Save SOA entries
    const { applicable, notApplicable } = await savePrivacyActSOA(companyId, answers, controls);
    
    // Create assurance tasks
    const { created, skipped } = await createPrivacyActAssuranceTasks(
      companyId,
      answers,
      controls,
      tasks
    );
    
    console.log('[PrivacyActSOAService] Complete! Applicable:', applicable, 'Not applicable:', notApplicable, 'Tasks created:', created, 'skipped:', skipped);
    
    return {
      soaSaved: true,
      controlsApplicable: applicable,
      controlsNotApplicable: notApplicable,
      tasksCreated: created,
      tasksSkipped: skipped
    };
  } catch (error) {
    console.error('[PrivacyActSOAService] Error in complete flow:', error);
    throw error;
  }
}

export default {
  fetchPAControls,
  fetchPATasks,
  getSuggestedTaskIds,
  savePrivacyActSOA,
  createPrivacyActAssuranceTasks,
  completePrivacyActSOA,
  QUESTION_TO_CONTROL_MAP,
};
